"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import {
    PiMapPin,
    PiPhone,
    PiClock,

    PiEnvelope,
    PiPaperPlaneRight,
    PiCheckCircle,
} from "react-icons/pi";
import TorchContactHero from "./TorchTypeContactHero";
import { HokContact } from "../../hok";
import OvanContact from "../../ovan/ovancontact/OvanContact";
import logger from "@/utils/logger";

// ==========================================
// SHARED TYPES & HELPERS
// ==========================================

const getContactInfo = (content: any) => [
    {
        icon: <PiMapPin className="w-6 h-6" />,
        title: "Location",
        text: content.locationText || "123 Business Park, Suite 100\nSan Francisco, CA 94105",
    },
    {
        icon: <PiPhone className="w-6 h-6" />,
        title: "Phone",
        text: content.phoneText || "+1 (555) 123-4567\nMon-Fri 9am-6pm PST",
    },
    {
        icon: <PiClock className="w-6 h-6" />,
        title: "Hours",
        text: content.hoursText || "Monday - Friday\n9:00 AM - 6:00 PM",
    },
];

const ContactForm = ({
    primaryColor,
    secondaryColor,
    isDark = false,
}: {
    primaryColor: string;
    secondaryColor: string;
    isDark?: boolean;
}) => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // Import homepageApi dynamically or require it
            const homepageApi = (await import("@/services/homepageApi")).default;
            const response = await homepageApi.submitQuery({
                name: formData.fullName,
                email: formData.email,
                message: formData.message
            });
            if (response.success) {
                setIsSubmitted(true);
                setFormData({ fullName: "", email: "", message: "" });
            } else {
                alert(response.message || "Failed to submit query");
            }
        } catch (err: any) {
            logger.error("Failed to submit query:", err);
            alert("Error submitting query. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className={`flex flex-col items-center justify-center py-12 text-center h-full rounded-2xl ${isDark ? 'bg-white/10 backdrop-blur-md' : 'bg-gray-50'}`}>
                <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                    }}
                >
                    <PiCheckCircle className="w-8 h-8 text-white" />
                </div>
                <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Message Sent!</h4>
                <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>We'll get back to you shortly.</p>
                <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-sm font-medium hover:underline"
                    style={{ color: primaryColor }}
                >
                    Send another message
                </button>
            </div>
        );
    }

    const inputClasses = isDark
        ? "w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 focus:border-white/50 focus:ring-2 focus:ring-white/20 transition-all outline-none text-white placeholder-gray-400"
        : "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none text-gray-900 placeholder-gray-400";

    const labelClasses = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label htmlFor="fullName" className={labelClasses}>Full Name</label>
                <input
                    type="text"
                    id="fullName"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="John Doe"
                    className={inputClasses}
                />
            </div>
            <div>
                <label htmlFor="email" className={labelClasses}>Email Address</label>
                <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    className={inputClasses}
                />
            </div>
            <div>
                <label htmlFor="message" className={labelClasses}>Message</label>
                <textarea
                    id="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    className={`${inputClasses} resize-none`}
                />
            </div>
            <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 px-6 rounded-xl font-bold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                    boxShadow: isDark ? `0 0 20px -5px ${primaryColor}50` : 'none'
                }}
            >
                {isSubmitting ? (
                    <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                    </>
                ) : (
                    <>
                        Send Message
                        <PiPaperPlaneRight className="w-5 h-5" />
                    </>
                )}
            </button>
        </form>
    );
};

// ==========================================
// COMPONENT 1: NORMAL VIEW (Traditional / Old)
// ==========================================
const NormalContact = ({ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }: any) => {
    const contactInfo = getContactInfo(content);

    return (
        <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="grid md:grid-cols-2">
                    {/* Left Info Column */}
                    <div className="p-8 md:p-12 bg-gray-50 flex flex-col justify-center">
                        <div className="mb-10">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily }}>
                                <EditableText
                                    value={content.title || "Get in Touch"}
                                    onChange={(val) => onContentChange?.({ ...content, title: val })}
                                    as="span"
                                />
                            </h2>
                            <p className="text-gray-600 text-lg">
                                <EditableText
                                    value={content.subtitle || "We are here to help and answer any questions you might have."}
                                    onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                                    as="span"
                                />
                            </p>
                        </div>

                        <div className="space-y-8">
                            {contactInfo.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                                        <p className="text-gray-600 whitespace-pre-line text-sm">{item.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Form Column */}
                    <div className="p-8 md:p-12 bg-white">
                        <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily }}>Send us a Message</h3>
                        <ContactForm primaryColor={primaryColor} secondaryColor={secondaryColor} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// COMPONENT 2: MODERN VIEW (Geometric / Blobs)
// ==========================================
const ModernContact = ({ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }: any) => {
    const contactInfo = getContactInfo(content);

    return (
        <>
            {/* Geometric Background Shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10" style={{ backgroundColor: primaryColor }} />
                <div className="absolute top-1/2 -left-40 w-60 h-60 rounded-full opacity-10" style={{ backgroundColor: secondaryColor }} />
                <div className="absolute bottom-0 right-1/4 w-40 h-40 rounded-full opacity-10" style={{ backgroundColor: primaryColor }} />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: `radial-gradient(${primaryColor} 1px, transparent 1px)`,
                        backgroundSize: "30px 30px",
                    }}
                />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily }}>
                        <EditableText
                            value={content.title || "Get in Touch"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                        />
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto" style={{ fontFamily }}>
                        <EditableText
                            value={content.subtitle || "Have questions? We'd love to hear from you."}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                        />
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-12 gap-8 items-stretch">
                    {/* Left - Contact Info Card */}
                    <div className="lg:col-span-4">
                        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 h-full">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily }}>Contact Information</h3>

                            <div className="space-y-6">
                                {contactInfo.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-green-50 transition-colors">
                                        <div
                                            className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white"
                                            style={{
                                                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                                            }}
                                        >
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1" style={{ fontFamily }}>{item.title}</h4>
                                            <p className="text-gray-600 text-sm whitespace-pre-line">{item.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Center - Office Image */}
                    <div className="lg:col-span-4 hidden lg:block">
                        <div className="h-full rounded-3xl overflow-hidden shadow-xl shadow-gray-200/50 relative group">
                            <img
                                src={content.centerImage || "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop"}
                                alt="Modern Office"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                                <div className="text-white">
                                    <h4 className="font-bold text-lg mb-1">Our Headquarters</h4>
                                    <p className="text-sm opacity-80">Come visit us anytime.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right - Contact Form Card */}
                    <div className="lg:col-span-4">
                        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 h-full">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily }}>Send us a Message</h3>
                            <p className="text-gray-600 mb-8 text-sm">We'll get back to you within 24 hours.</p>
                            <ContactForm primaryColor={primaryColor} secondaryColor={secondaryColor} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// ==========================================
// COMPONENT 3: CONTACT HERO (Telescope Style)
// ==========================================

// ==========================================
// COMPONENT 3: CONTACT HERO (Telescope Style)
// ==========================================
const ContactHero = ({ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }: any) => {
    return (
        <TorchContactHero
            content={content}
            onContentChange={onContentChange}
            primaryColor={primaryColor}
            fontFamily={fontFamily}
        />
    );
};

// ==========================================
// MAIN COMPONENT EXPORT
// ==========================================
export default function ContactSection({
    content,
    onContentChange,
    isSelected,
}: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#16a34a";
    const secondaryColor = content.colorPalette?.secondary || "#15803d";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const variantId = content.variant || 'v1';
    const isOvan = variantId === 'ovan';
    const isHok = variantId === 'hok';
    const isV3 = variantId === 'v3' || variantId === 'contact-v3';
    const isV2 = variantId === 'v2' || variantId === 'contact-v2';
    const isV1 = !isV2 && !isV3 && !isHok && !isOvan;

    const renderVariant = () => {
        if (isOvan) return <OvanContact {...{ content, onContentChange, isSelected }} />;
        if (isHok) return <HokContact {...{ content, onContentChange, isSelected }} />;
        if (isV3) return <ContactHero {...{ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }} />;
        if (isV2) return <ModernContact {...{ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }} />;
        return <NormalContact {...{ content, onContentChange, isSelected, primaryColor, secondaryColor, fontFamily }} />;
    };

    return (
        <section
            className={`relative overflow-hidden w-full ${isSelected ? "ring-2 ring-blue-500 z-50" : ""} ${isOvan ? "" : (isHok ? "py-10 sm:py-14 bg-gray-50" : (isV1 ? "bg-gray-50 py-20 px-6" : (isV2 ? "bg-[#f0fdf4] py-20 px-6" : "")))}`}
            style={{ fontFamily }}
        >
            {renderVariant()}
        </section>
    );
}

// Standalone export for backward compatibility if needed elsewhere
export function ModernContactSection(props: any) {
    return <ContactSection content={{ variant: 'v2', ...props }} onContentChange={() => { }} isSelected={false} />;
}
